import { useState, useEffect } from 'react';
import { Check, KeyboardArrowDown } from '@mui/icons-material';
import { Box, Menu, MenuItem, Stack } from '@mui/material';
import Button from 'domains/core/components/Button';
import { themeColors } from 'colors';

export type MultiSelectOption<T> = {
    value: T;
    label: string;
};

export type MultiSelectDropdownProps<T> = {
    /** Array of options to display in the dropdown */
    options: MultiSelectOption<T>[];
    /** Currently selected values */
    selectedValues: T[];
    /** Callback when Apply button is clicked */
    onApply: (values: T[]) => void;
    /** Label for the dropdown button (default: "Status") */
    label?: string;
    /** Prefix for data-testid attributes (default: "multiselect") */
    testIdPrefix?: string;
    /** ID for the menu element (default: "multiselect-menu") */
    menuId?: string;
    /** Max height for the dropdown menu */
    maxHeight?: string;
};

const MultiSelectDropdown = <T extends string>({
    options,
    selectedValues,
    onApply,
    label = 'Status',
    testIdPrefix = 'multiselect',
    menuId = 'multiselect-menu',
    maxHeight,
}: MultiSelectDropdownProps<T>) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [localSelectedValues, setLocalSelectedValues] = useState<T[]>(selectedValues);
    const isOpen = Boolean(anchorEl);

    // Update local state when selectedValues prop changes
    useEffect(() => {
        setLocalSelectedValues(selectedValues);
    }, [selectedValues]);

    // Opens the dropdown menu when the button is clicked
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Closes the dropdown menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Toggles the selection of an option (selects if unselected, deselects if already selected)
    // Updates only local state - changes are not applied until Apply button is clicked
    const handleToggleValue = (value: T) => {
        setLocalSelectedValues((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    };

    // Applies the selected filters and closes the dropdown
    const handleApply = () => {
        onApply(localSelectedValues);
        handleClose();
    };

    // Clears all selected values from local state
    // Does not apply changes - user must click Apply to update the filter
    const handleClear = () => {
        setLocalSelectedValues([]);
    };

    const hasSelectedValues = localSelectedValues.length > 0;

    return (
        <>
            <Button
                onClick={handleClick}
                endIcon={<KeyboardArrowDown />}
                sx={{
                    textTransform: 'capitalize',
                    color: hasSelectedValues ? themeColors.action.active : 'inherit',
                }}
                variant="text"
                aria-controls={isOpen ? menuId : undefined}
                aria-expanded={isOpen ? 'true' : undefined}
                aria-haspopup="menu"
                data-testid={`${testIdPrefix}-button`}
                role="button"
            >
                {label}
            </Button>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        minWidth: '200px',
                        ...(maxHeight && { maxHeight }),
                        mt: 1,
                    },
                }}
                MenuListProps={{
                    role: 'menu',
                }}
            >
                <Box data-testid={`${testIdPrefix}-menu`} sx={{ display: 'contents' }}>
                    {options.map((option) => {
                        const isSelected = localSelectedValues.includes(option.value);
                        return (
                            <MenuItem
                                key={option.value}
                                onClick={() => handleToggleValue(option.value)}
                                selected={isSelected}
                                data-testid={`${testIdPrefix}-option-${option.value.toLowerCase()}`}
                                role="menuitemcheckbox"
                                aria-checked={isSelected}
                                sx={{
                                    textTransform: 'capitalize',
                                    '&.Mui-selected': {
                                        backgroundColor: 'white',
                                        color: 'action.active',
                                    },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                {option.label}
                                {isSelected && (
                                    <Check
                                        data-testid={`${testIdPrefix}-check-${option.value.toLowerCase()}`}
                                        sx={{
                                            color: '#00845B',
                                            fontSize: '20px',
                                            ml: 2,
                                        }}
                                    />
                                )}
                            </MenuItem>
                        );
                    })}
                    <Box
                        sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 0.5 }}
                        data-testid={`${testIdPrefix}-actions`}
                    >
                        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClear}
                                data-testid={`${testIdPrefix}-clear-button`}
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                }}
                                disabled={localSelectedValues.length === 0}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleApply}
                                data-testid={`${testIdPrefix}-apply-button`}
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    bgcolor: '#00845B',
                                    '&:hover': {
                                        bgcolor: '#006B47',
                                    },
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Menu>
        </>
    );
};

export default MultiSelectDropdown;
